import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.js';

/**
 * Small data hook: fetch on mount + whenever `deps` change, with an explicit
 * refresh function and no stale-response races.
 */
export function useApi(path, { params, deps = [], skip = false } = {}) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const requestId = useRef(0);
  const paramsKey = JSON.stringify(params || {});

  useEffect(() => {
    if (skip || !path) {
      setLoading(false);
      return undefined;
    }
    const id = requestId.current + 1;
    requestId.current = id;
    const controller = new AbortController();
    setLoading(true);
    api
      .get(path, JSON.parse(paramsKey), { signal: controller.signal })
      .then((payload) => {
        if (requestId.current !== id) return;
        setData(payload?.data ?? payload);
        const { data: _d, ...rest } = payload || {};
        setMeta(Object.keys(rest).length ? rest : null);
        setError(null);
      })
      .catch((err) => {
        if (err.name === 'AbortError' || requestId.current !== id) return;
        setError(err);
      })
      .finally(() => {
        if (requestId.current === id) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, paramsKey, skip, tick, ...deps]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { data, meta, loading, error, refresh, setData };
}

/** Poll an endpoint on an interval (used for the live dispatch board). */
export function usePoll(path, { params, ms = 30_000, skip = false } = {}) {
  const result = useApi(path, { params, skip });
  const paramsKey = JSON.stringify(params || {});
  const { refresh } = result;
  useEffect(() => {
    if (skip) return undefined;
    const id = setInterval(refresh, ms);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ms, skip, paramsKey, refresh]);
  return result;
}

export function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function useLocalStorage(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : JSON.parse(raw);
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore quota errors */
    }
  }, [key, value]);
  return [value, setValue];
}

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** Global keyboard shortcut, e.g. useHotkey('k', { meta: true }, open). */
export function useHotkey(key, { meta = false, shift = false } = {}, handler) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key.toLowerCase() !== key.toLowerCase()) return;
      if (meta && !(event.metaKey || event.ctrlKey)) return;
      if (shift && !event.shiftKey) return;
      const tag = document.activeElement?.tagName;
      if (!meta && ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      event.preventDefault();
      handler(event);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [key, meta, shift, handler]);
}

export function useToggle(initial = false) {
  const [on, setOn] = useState(initial);
  return [on, useCallback(() => setOn((v) => !v), []), setOn];
}
